/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule form utilities
 *
 * @file quickScheduleFormUtils.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import { TIMEFIELD_CONSTANTS } from '../../common/constants';
import { matchTimezoneToOptions, parseDate } from '../../common/dateTimeUtils';
import { buildQuickScheduleAssignments } from './quickScheduleReviewUtils';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const EMPTY_QUICK_SCHEDULE_FORM_VALUES = {
  dueDays: [],
  dueTime: '',
  timeZone: '',
  startDate: '',
  endDate: ''
};

function toLocalCalendarDate(date, dayOffset = 0) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayOffset);
}

function getLocalDayMs(date) {
  return toLocalCalendarDate(date).getTime();
}

function getLocalCalendarDayDiff(laterDate, earlierDate) {
  return Math.round((getLocalDayMs(laterDate) - getLocalDayMs(earlierDate)) / MS_PER_DAY);
}

export function resolveDefaultDueTime(dueTime) {
  if (dueTime) {
    return dueTime;
  }

  return TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME;
}

export function resolveDefaultTimeZone(timeZone, timeZoneOptions = []) {
  // Keep an explicit value from parent/assignment as-is.
  if (timeZone) {
    return timeZone;
  }

  // No explicit value: match the browser timezone, or leave empty if no match.
  return matchTimezoneToOptions(timeZoneOptions) || '';
}

/**
 * Default start date: an explicit value from parent/assignment as-is, otherwise the
 * course-bounded minimum (day after course start, or today if the course already started).
 */
export function resolveDefaultStartDate(startDate, courseDateBounds = {}) {
  if (startDate) {
    return startDate;
  }
  // eslint-disable-next-line no-use-before-define
  const minDate = getCourseBoundedStartMinDate(courseDateBounds);

  // eslint-disable-next-line no-use-before-define
  return minDate ? toStartDateIso(minDate) : EMPTY_QUICK_SCHEDULE_FORM_VALUES.startDate;
}

export function buildFormDefaultValues(initialValues = {}, timeZoneOptions = [], courseDateBounds = {}) {
  const {
    dueDays,
    dueTime,
    timeZone,
    startDate,
    endDate
  } = initialValues;

  return {
    ...EMPTY_QUICK_SCHEDULE_FORM_VALUES,
    dueDays: dueDays ?? EMPTY_QUICK_SCHEDULE_FORM_VALUES.dueDays,
    dueTime: resolveDefaultDueTime(dueTime),
    timeZone: resolveDefaultTimeZone(timeZone, timeZoneOptions),
    startDate: resolveDefaultStartDate(startDate, courseDateBounds),
    endDate: endDate ?? EMPTY_QUICK_SCHEDULE_FORM_VALUES.endDate
  };
}

/**
 * Start date ISO for the picked calendar day, preserving local time-of-day
 * from the existing value (or now when unset).
 */
export function toStartDateIso(pickedDate, referenceIso = '') {
  if (!pickedDate || Number.isNaN(pickedDate.getTime())) {
    return '';
  }

  const reference = referenceIso ? new Date(referenceIso) : new Date();

  if (referenceIso && Number.isNaN(reference.getTime())) {
    return '';
  }

  return new Date(
    pickedDate.getFullYear(),
    pickedDate.getMonth(),
    pickedDate.getDate(),
    reference.getHours(),
    reference.getMinutes(),
    reference.getSeconds(),
    reference.getMilliseconds()
  ).toISOString();
}

/**
 * End date ISO as start + whole 24h periods for each local calendar day after start.
 */
export function toEndDateIso(pickedEndDate, startDateIso) {
  if (!pickedEndDate || Number.isNaN(pickedEndDate.getTime()) || !startDateIso) {
    return '';
  }

  const start = new Date(startDateIso);

  if (Number.isNaN(start.getTime())) {
    return '';
  }

  const dayDiff = getLocalCalendarDayDiff(pickedEndDate, start);

  if (dayDiff < 1) {
    return '';
  }

  return new Date(start.getTime() + dayDiff * MS_PER_DAY).toISOString();
}

/** Local calendar Date for the picker from a stored ISO string. */
export function parseDateForPicker(isoString) {
  if (!isoString) {
    return null;
  }

  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return toLocalCalendarDate(date);
}

/** Earliest selectable end date: local calendar day after start, or tomorrow. */
export function getEndDateMinDate(startDateValue) {
  if (startDateValue) {
    const start = parseDateForPicker(startDateValue);

    if (start) {
      return toLocalCalendarDate(start, 1);
    }
  }

  return toLocalCalendarDate(new Date(), 1);
}

/** Converts a stored ISO date to a local calendar Date for picker comparisons. */
function toLocalCalendarDateFromIso(isoValue) {
  if (!isoValue) {
    return null;
  }

  const dateOnly = String(isoValue).includes('T') ? String(isoValue).split('T')[0] : isoValue;
  const date = parseDate(dateOnly);

  if (!date) {
    return null;
  }

  return toLocalCalendarDate(date);
}

/** Earliest selectable start date within course bounds (day after course start, not before today). */
export function getCourseBoundedStartMinDate(courseDateBounds = {}) {
  const today = toLocalCalendarDate(new Date());
  const courseStart = toLocalCalendarDateFromIso(courseDateBounds.courseStartDate);

  if (!courseStart) {
    return today;
  }

  const dayAfterCourseStart = toLocalCalendarDate(courseStart, 1);

  return dayAfterCourseStart > today ? dayAfterCourseStart : today;
}

/** Latest selectable end date within course bounds (day before course end). */
export function getCourseBoundedEndMaxDate(courseDateBounds = {}) {
  const courseEnd = toLocalCalendarDateFromIso(courseDateBounds.courseEndDate);

  if (!courseEnd) {
    return null;
  }

  return toLocalCalendarDate(courseEnd, -1);
}

/**
 * Latest selectable start date within course bounds.
 * Two days before course end so end date (start + 1) can still fit before course end.
 */
export function getCourseBoundedStartMaxDate(courseDateBounds = {}) {
  const courseEnd = toLocalCalendarDateFromIso(courseDateBounds.courseEndDate);

  if (!courseEnd) {
    return null;
  }

  return toLocalCalendarDate(courseEnd, -2);
}

/** Earliest selectable end date respecting start date and course bounds. */
export function getCourseBoundedEndMinDate(startDateValue) {
  return getEndDateMinDate(startDateValue);
}

/** True when the start date is before the course-bounded minimum. */
export function isStartDateBeforeCourseMin(startDateIso, courseDateBounds = {}) {
  if (!startDateIso) {
    return false;
  }

  const minDate = getCourseBoundedStartMinDate(courseDateBounds);
  const start = parseDateForPicker(startDateIso);

  if (!start) {
    return false;
  }

  return getLocalDayMs(start) < getLocalDayMs(minDate);
}

/** True when the start date is after the course-bounded maximum. */
export function isStartDateAfterCourseMax(startDateIso, courseDateBounds = {}) {
  const maxDate = getCourseBoundedStartMaxDate(courseDateBounds);

  if (!maxDate || !startDateIso) {
    return false;
  }

  const start = parseDateForPicker(startDateIso);

  if (!start) {
    return false;
  }

  return getLocalDayMs(start) > getLocalDayMs(maxDate);
}

/** True when the end date is after the course-bounded maximum. */
export function isEndDateAfterCourseMax(endDateIso, courseDateBounds = {}) {
  const maxDate = getCourseBoundedEndMaxDate(courseDateBounds);

  if (!maxDate || !endDateIso) {
    return false;
  }

  const end = parseDateForPicker(endDateIso);

  if (!end) {
    return false;
  }

  return getLocalDayMs(end) > getLocalDayMs(maxDate);
}

/** react-hook-form validate rule: start date must stay within course bounds. */
export function buildStartDateInRangeValidator(courseDateBounds = {}, errorMessage = '') {
  return (value) => {
    if (!value) {
      return true;
    }

    const hasCourseBounds = courseDateBounds.courseStartDate || courseDateBounds.courseEndDate;

    if (!hasCourseBounds) {
      return true;
    }

    const message = errorMessage || 'Choose a date within your course schedule';

    if (isStartDateBeforeCourseMin(value, courseDateBounds)) {
      return message;
    }

    if (isStartDateAfterCourseMax(value, courseDateBounds)) {
      return message;
    }

    return true;
  };
}

/** react-hook-form validate rule: end date must be before course end. */
export function buildEndDateInRangeValidator(courseDateBounds = {}, errorMessage = '') {
  return (value) => {
    if (!value) {
      return true;
    }

    const hasCourseBounds = courseDateBounds.courseEndDate;

    if (!hasCourseBounds) {
      return true;
    }

    const message = errorMessage || 'Choose a date within your course schedule';

    return isEndDateAfterCourseMax(value, courseDateBounds) ? message : true;
  };
}

/** Whether a stored end date is before the local calendar day after start. */
export function isEndDateBeforeMinimum(endDateIso, startDateIso) {
  if (!endDateIso || !startDateIso) {
    return false;
  }

  const end = parseDateForPicker(endDateIso);
  const start = parseDateForPicker(startDateIso);

  if (!end || !start) {
    return false;
  }

  return getLocalCalendarDayDiff(end, start) < 1;
}

export function hasMatchingDueDaysInDateRange(formValues = {}) {
  const {
    dueDays = [],
    startDate = '',
    endDate = ''
  } = formValues;

  if (!dueDays.length || !startDate || !endDate) {
    return true;
  }

  return buildQuickScheduleAssignments(formValues).length > 0;
}

export const isSubmitButtonDisabled = ({
  isSubmitting,
  isValid,
  hasValidDueDayRange = true
}) => (
  isSubmitting || !isValid || !hasValidDueDayRange
);
